import React, { useState, useMemo } from 'react';
import { Card, Button, Form, Table, Input, Typography, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseReturnService, type PurchaseReturnLineDTO } from '@/services/purchaseReturn.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { headerProjectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_RETURN } from '@/constants/document-entity-types';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';

const { Title } = Typography;

export default function PurchaseReturnEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [lineQuantities, setLineQuantities] = useState<Record<string, number>>({});
  const { isCriticalCode } = useCriticalItemLookup();

  const { data: pr, isLoading: loadingPr } = useQuery({
    queryKey: ['purchase-return', id],
    queryFn: () => purchaseReturnService.findById(id!),
    enabled: !!id,
  });

  const { data: sources = [] } = useQuery({
    queryKey: ['purchase-return-returnable-sources', pr?.grn_id],
    queryFn: () => purchaseReturnService.getReturnableSources(pr?.grn_id ? { grn_id: pr.grn_id } : undefined),
    enabled: !!pr?.grn_id,
  });

  const returnableByGrnItemId = useMemo(() => new Map(sources.map((s) => [s.grn_item_id, s.returnable_quantity])), [sources]);

  React.useEffect(() => {
    if (pr?.lines) {
      const q: Record<string, number> = {};
      for (const line of pr.lines) {
        if (line.grn_item_id) q[line.grn_item_id] = line.return_quantity ?? 0;
      }
      setLineQuantities(q);
    }
  }, [pr?.lines]);

  const updateMutation = useMutation({
    mutationFn: (payload: {
      return_date?: string;
      department_id?: string;
      return_reason?: string;
      remarks?: string;
      lines: Array<{ grn_item_id: string; return_quantity: number; return_reason?: string }>;
    }) => purchaseReturnService.update(id!, payload),
    onSuccess: () => {
      message.success('Purchase return updated');
      queryClient.invalidateQueries({ queryKey: ['purchase-return', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
      navigate(`/scm/procurement/purchase-returns/${id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || 'Failed to update');
    },
  });

  const handleQuantityChange = (grnItemId: string, value: number) => {
    setLineQuantities((prev) => ({ ...prev, [grnItemId]: value }));
  };

  const onFinish = async () => {
    try {
      await form.validateFields(['return_date', 'department_id']);
    } catch {
      return;
    }
    const values = form.getFieldsValue();
    if (!pr?.lines?.length) {
      message.error('No lines to update');
      return;
    }
    const lines = pr.lines
      .filter((l) => l.grn_item_id)
      .map((line) => ({
        grn_item_id: line.grn_item_id!,
        return_quantity: lineQuantities[line.grn_item_id!] ?? 0,
        return_reason: values.return_reason || undefined,
      }))
      .filter((l) => l.return_quantity > 0);
    if (lines.length === 0) {
      message.error('Enter at least one return quantity');
      return;
    }
    const maxByLine = lines.map((l) => ({ ...l, max: returnableByGrnItemId.get(l.grn_item_id) ?? 0 }));
    const invalid = maxByLine.find((l) => l.return_quantity > l.max);
    if (invalid) {
      message.error(`Return quantity for line exceeds returnable (${invalid.max})`);
      return;
    }
    updateMutation.mutate({
      return_date: values.return_date,
      department_id: values.department_id,
      return_reason: values.return_reason,
      remarks: values.remarks,
      lines,
    });
  };

  if (!id || (loadingPr && !pr)) return null;
  if (!pr) return <Card><p>Purchase return not found.</p></Card>;
  if (pr.status !== 'Draft') {
    return (
      <Card>
        <p>Only Draft purchase returns can be edited.</p>
        <Button onClick={() => navigate(`/scm/procurement/purchase-returns/${id}`)}>View</Button>
      </Card>
    );
  }

  const columns: ColumnsType<PurchaseReturnLineDTO & { returnable?: number }> = [
    {
      title: 'Item',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 140,
      render: (code: string, r) => (
        <ItemCodeWithCritical code={code} critical={isCriticalCode(r.item_code)} />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'item_description',
      key: 'item_description',
      render: (desc: string, r) => (
        <>
          <div>{desc}</div>
          <LineSpecificationsText value={r.specifications} />
        </>
      ),
    },
    {
      title: 'Supplier Description',
      dataIndex: 'supplier_description',
      key: 'supplier_description',
      render: (v: string, r: PurchaseReturnLineDTO) => v || r.item_description || '—',
    },
    { title: 'Returnable', key: 'returnable', width: 100, render: (_, r) => (r.grn_item_id ? returnableByGrnItemId.get(r.grn_item_id) ?? '—' : '—') },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 60 },
    {
      title: 'Return Qty',
      key: 'return_quantity',
      width: 120,
      render: (_, record) => {
        const grnItemId = record.grn_item_id;
        if (!grnItemId) return '—';
        const max = returnableByGrnItemId.get(grnItemId) ?? 0;
        return (
          <Input
            type="number"
            min={0}
            max={max}
            value={lineQuantities[grnItemId] ?? ''}
            onChange={(e) => handleQuantityChange(grnItemId, Number(e.target.value) || 0)}
            placeholder="0"
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/scm/procurement/purchase-returns/${id}`)} style={{ marginBottom: 16 }}>
        Back to Purchase Return
      </Button>

      <Title level={4} style={{ marginBottom: 24 }}>
        Edit Purchase Return {pr.return_number}
      </Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          return_date: pr.return_date,
          department_id: pr.department_id ?? undefined,
          return_reason: pr.return_reason ?? '',
          remarks: pr.remarks ?? '',
        }}
      >
        <Form.Item label="Project">
          <Input readOnly value={headerProjectLabel(pr as any, pr.lines as any)} />
        </Form.Item>
        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>
        <Form.Item name="return_date" label="Return Date" rules={[{ required: true }]}>
          <Input type="date" />
        </Form.Item>
        <Form.Item name="return_reason" label="Return reason">
          <Input placeholder="Reason" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="Remarks" />
        </Form.Item>
        <Table
          size="small"
          rowKey="id"
          dataSource={(pr.lines ?? []).map((l) => ({ ...l, returnable: l.grn_item_id ? returnableByGrnItemId.get(l.grn_item_id) : undefined }))}
          columns={columns}
          rowClassName={(r) =>
            getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
          }
          pagination={false}
          style={{ marginBottom: 16 }}
        />
        <Button type="primary" onClick={onFinish} loading={updateMutation.isPending}>
          Save
        </Button>
      </Form>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_RETURN}
        entityId={id}
        queryKeyPrefix={['documents', 'purchase-return']}
      />
    </div>
  );
}
